/**
 * Navigation model shared by the site header/footer and the admin editor.
 *
 * Links live in the `nav_items` table so admins can add, rename, reorder, hide
 * or remove menu entries and have them go live instantly (no code deploy).
 * The constants below mirror the seeded rows and are used as a render-time
 * fallback while the query loads (and if the database is unreachable).
 */

export type NavArea = "header" | "footer";

export type NavItem = {
  id: string;
  area: NavArea;
  column_label: string | null;
  label: string;
  href: string;
  sort_order: number;
  is_visible: boolean;
  open_in_new: boolean;
};

export const DEFAULT_HEADER_NAV: Pick<NavItem, "label" | "href">[] = [
  { label: "Shop", href: "/shop" },
  { label: "How it works", href: "/how-it-works" },
  { label: "Night Golf", href: "/night-golf" },
  { label: "Mini Golf", href: "/mini-golf" },
  { label: "Gifts", href: "/gifts" },
  { label: "Events", href: "/events" },
  { label: "Amazon", href: "/amazon" },
  { label: "Blog", href: "/blog" },
];

export const DEFAULT_FOOTER_NAV: { column: string; items: Pick<NavItem, "label" | "href">[] }[] = [
  {
    column: "Shop",
    items: [
      { label: "All products", href: "/shop" },
      { label: "Bundles", href: "/bundles" },
      { label: "Gifts", href: "/gifts" },
      { label: "Events & bulk", href: "/events" },
      { label: "Buy on Amazon", href: "/amazon" },
    ],
  },
  {
    column: "Learn",
    items: [
      { label: "How it works", href: "/how-it-works" },
      { label: "Night Golf", href: "/night-golf" },
      { label: "Blog", href: "/blog" },
      { label: "Reviews", href: "/reviews" },
      { label: "FAQ", href: "/faq" },
    ],
  },
  {
    column: "Company",
    items: [
      { label: "About", href: "/about" },
      { label: "Contact", href: "/contact" },
      { label: "Shipping & returns", href: "/shipping-returns" },
      { label: "Privacy", href: "/legal/privacy" },
      { label: "Terms", href: "/legal/terms" },
      { label: "Cookies", href: "/legal/cookies" },
      { label: "Accessibility", href: "/legal/accessibility" },
    ],
  },
];

/** True when a href should be treated as an external link (new tab / plain <a>). */
export function isExternalHref(href: string): boolean {
  return /^(https?:)?\/\//i.test(href) || href.startsWith("mailto:") || href.startsWith("tel:");
}

/** Group footer items into ordered columns, preserving first-seen column order. */
export function groupFooterItems(items: NavItem[]): { column: string; items: NavItem[] }[] {
  const out: { column: string; items: NavItem[] }[] = [];
  for (const item of items) {
    const column = (item.column_label ?? "").trim() || "More";
    let bucket = out.find((b) => b.column === column);
    if (!bucket) {
      bucket = { column, items: [] };
      out.push(bucket);
    }
    bucket.items.push(item);
  }
  return out;
}

/** Normalize a user-entered link into a site path or external URL. */
export function normalizeHref(raw: string): string {
  const value = raw.trim();
  if (!value) return "/";
  if (isExternalHref(value)) return value;
  return value.startsWith("/") ? value : `/${value}`;
}

/** Slugify a page title into a URL-safe slug. */
export function slugifyPageTitle(raw: string): string {
  return raw
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}
